import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePrinterStore } from 'stores/printerStore';
import { ROUTES } from 'utils/constants';
import {
  requestBluetoothDevice,
  requestUsbDevice,
  type SavedPrinter,
} from 'utils/printerUtils';
import Button from 'components/Button';
import PageTitle from 'components/PageTitle';
import CompanyGuard from 'components/CompanyGuard';
import { Bluetooth, Usb, ArrowRight, Printer } from 'lucide-react';
import styles from './PrinterSetup.module.scss';

const PrinterSetup: React.FC = () => {
  const navigate = useNavigate();
  const setSavedPrinter = usePrinterStore((s) => s.setSavedPrinter);
  const setActiveDevice = usePrinterStore((s) => s.setActiveDevice);
  const [error, setError] = useState<string | null>(null);

  const handleBluetoothConnect = async () => {
    try {
      const device = await requestBluetoothDevice();
      if (device) {
        const newPrinter: SavedPrinter = {
          type: 'bluetooth',
          id: device.id,
          name: device.name || 'Bluetooth Printer',
        };
        void setSavedPrinter(newPrinter);
        setActiveDevice(device);
        navigate(ROUTES.POS.ROOT);
      }
    } catch (err) {
      if ((err as Error).name !== 'NotFoundError') {
        setError((err as Error).message);
      }
    }
  };

  const handleUsbConnect = async () => {
    try {
      const device = await requestUsbDevice();
      if (device) {
        const newPrinter: SavedPrinter = {
          type: 'usb',
          id: `${device.vendorId}_${device.productId}`,
          name: device.productName || 'USB Printer',
        };
        void setSavedPrinter(newPrinter);
        setActiveDevice(device);
        navigate(ROUTES.POS.ROOT);
      }
    } catch (err) {
      if ((err as Error).name !== 'NotFoundError') {
        setError((err as Error).message);
      }
    }
  };

  return (
    <CompanyGuard>
      <div className={styles.container}>
        <PageTitle />
        <div className={styles.setupCard}>
          <div className={styles.header}>
            <div className={styles.iconWrapper}>
              <Printer size={32} />
            </div>
            <h2>Configure Your Receipt Printer</h2>
            <p>Select a connection method to get started with printing.</p>
          </div>

          {error && <div className={styles.errorAlert}>{error}</div>}

          <div className={styles.options}>
            <button
              className={styles.optionCard}
              onClick={() => {
                void handleBluetoothConnect();
              }}
            >
              <div className={styles.optionIcon}>
                <Bluetooth size={24} />
              </div>
              <div className={styles.optionInfo}>
                <h3>Bluetooth Printer</h3>
                <p>Wireless connection for mobile and desktop</p>
              </div>
              <ArrowRight size={20} className={styles.arrow} />
            </button>

            <button
              className={styles.optionCard}
              onClick={() => {
                void handleUsbConnect();
              }}
            >
              <div className={styles.optionIcon}>
                <Usb size={24} />
              </div>
              <div className={styles.optionInfo}>
                <h3>USB Printer</h3>
                <p>Direct cable connection for desktop</p>
              </div>
              <ArrowRight size={20} className={styles.arrow} />
            </button>
          </div>

          <div className={styles.footer}>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(ROUTES.POS.SETTINGS)}
              className={styles.advancedLink}
            >
              Advanced Settings <ArrowRight size={14} />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(ROUTES.POS.ROOT)}
            >
              Skip for now
            </Button>
          </div>
        </div>
      </div>
    </CompanyGuard>
  );
};

export default PrinterSetup;
